import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPeopleSimple } from '@fortawesome/pro-solid-svg-icons';
import { Form, Spinner } from 'react-bootstrap-5';
import { getDemographicsReport } from 'bundles/connect/helpers/prospect_api';
import { Button } from 'components/ui/button';
import type { DemographicsReport } from 'bundles/connect/types';
import DemographicsTable from './demographics_table';

type Props = {
  insightsPropertyId: string;
  latitude: number;
  longitude: number;
};

export const RADII_COLORS = ['#6685e0', '#e5bf77', '#82d5b5'];

export default function DemographicsSection({ insightsPropertyId, latitude, longitude }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [demographicsReport, setDemographicsReport] = useState<DemographicsReport | null>(null);
  const [inputs, setInputs] = useState<string[]>([]);

  const handleSubmit = () => {
    if (window.gtag) window.gtag('event', 'connect_off_market_property_added_to_call_list');
    getDemographicsReport(insightsPropertyId, inputs).then((res: DemographicsReport) => {
      setDemographicsReport(res);
      setHasFetched(true);
    });
  };

  const handleInputChange = (index: number, val: string) => {
    const newValues = [...inputs];
    newValues[index] = val;
    setInputs(newValues);
  };

  const { radii: rawRadii = [] } = demographicsReport || {};

  const { radiiInMeters, googleMapOptions } = useMemo(() => {
    const radiiLengths = rawRadii.map(r => r.radiusInMiles);
    const meters = radiiLengths.map(miles => miles * 1609.34);
    const options = {
      center: {
        lat: latitude,
        lng: longitude,
      },
      draggable: false,
      zoomControl: false,
    };

    return {
      googleMapOptions: options,
      radiiInMeters: meters,
    };
  }, [rawRadii]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasFetched) {
          if (window.gtag) {
            window.gtag('event', 'connect_off_market_demographics_generated', { propertyId: insightsPropertyId });
          }
          getDemographicsReport(insightsPropertyId).then((res) => {
            setDemographicsReport(res);
            setHasFetched(true);
          });
        }
      },
      {
        root: null,
        threshold: 0.5,
      }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [hasFetched, insightsPropertyId]);

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex align-items-center gap-2" ref={containerRef}>
        <FontAwesomeIcon className="text-primary" icon={faPeopleSimple} />
        <h6 className="my-0 fw-bolder">Demographics</h6>
      </div>
      {!hasFetched && (
        <div className="text-center p-3">
          <Spinner animation="border" variant="secondary" />
        </div>
      )}
      {demographicsReport && (
        <>
          <div
            className="mb-4"
            data-controller="googleMap"
            data-googlemap-options-value={JSON.stringify(googleMapOptions)}
            data-googlemap-radii-colors-value={JSON.stringify(RADII_COLORS)}
            data-googlemap-radii-value={JSON.stringify(radiiInMeters)}
            key={JSON.stringify(radiiInMeters)} // Used to trick the googleMapController to run connect again
            style={{ height: '350px' }}
          />
          <div
            className="d-flex flex-column flex-sm-row gap-3 mb-4"
          >
            {[0, 1, 2].map(index => (
              <div key={index}>
                <Form.Group controlId={`radius-${index}`}>
                  <Form.Label>{`Radius ${index + 1}`}</Form.Label>
                  <Form.Control
                    min="0"
                    name="radii[]"
                    step="any"
                    type="number"
                    value={inputs[index] || ''}
                    onChange={e => handleInputChange(index, e.target.value)}
                  />
                </Form.Group>
              </div>
            ))}
            <Button className="mt-auto" label="Update Demographics" onClick={handleSubmit} />
          </div>
          <DemographicsTable demographicsReport={demographicsReport} />
        </>
      )}
    </div>
  );
}
